/**
 * useEntityExplorer Hook
 * Manages all navigation, selection, and state logic
 * This is the "brain" of the modal component
 */

import { useState, useCallback, useRef } from 'react';
import type {
  EntityExplorerConfig,
  ExplorerNode,
  BreadcrumbNode,
} from '../types/entity-explorer.types';
import * as api from '../services/entityExplorerApi.service';

/**
 * Complete state shape
 */
interface ExplorerState {
  currentNode: ExplorerNode | 'HOME';
  children: ExplorerNode[];
  breadcrumbTrail: BreadcrumbNode[];
  selection: Map<string, ExplorerNode>; // For multiple: true mode
  page: number;
  limit: number;
  total: number;
  sortField: 'name' | 'entityType' | 'createdBy' | 'createdAt';
  sortOrder: 'asc' | 'desc';
  loading: boolean;
  error: string | null;
}

/**
 * Main hook - manages all explorer state and navigation
 */
export function useEntityExplorer(config: EntityExplorerConfig) {
  // Main state
  const [state, setState] = useState<ExplorerState>({
    currentNode: 'HOME',
    children: [],
    breadcrumbTrail: [{ _id: null, name: 'Folders', entityType: 'HOME' }],
    selection: new Map(),
    page: 1,
    limit: 20,
    total: 0,
    sortField: 'name',
    sortOrder: 'asc',
    loading: false,
    error: null,
  });

  // Breadcrumb cache (useRef so it survives re-renders)
  // Maps entityId → breadcrumb trail
  const breadcrumbCache = useRef<Map<string, BreadcrumbNode[]>>(new Map());
  const queryStateRef = useRef<{
    page: number;
    limit: number;
    sortField: 'name' | 'entityType' | 'createdBy' | 'createdAt';
    sortOrder: 'asc' | 'desc';
    searchTerm: string;
  }>({
    page: 1,
    limit: 20,
    sortField: 'name',
    sortOrder: 'asc',
    searchTerm: '',
  });
  // Ignore stale responses when a newer navigateTo has already started
  const navigationGenerationRef = useRef(0);

  /**
   * Resolve breadcrumb trail with caching
   * If we've seen this node before, return cached trail
   * Otherwise fetch from API and cache it
   */
  const resolveBreadcrumb = useCallback(
    async (node: ExplorerNode | 'HOME'): Promise<BreadcrumbNode[]> => {
      if (node === 'HOME') {
        return [{ _id: null, name: 'Folders', entityType: 'HOME' }];
      }

      // Check cache first
      const cached = breadcrumbCache.current.get(node.id);
      if (cached) return cached;

      try {
       

        // For workflows, we need to find the experiment ID (stored in breadcrumb.entityId)
        let idToFetch = node.id;
        if (node.entityType === 'WETLABWORKFLOW' || node.entityType === 'COMPUTATIONALWORKFLOW') {
          // Try multiple ways to get experiment ID from version data
          const raw = node.raw as any;
          
          const experimentId = raw?.experiment?._id 
                            ?? raw?.experiment 
                            ?? raw?.experimentId
                            ?? raw?.expId
                            ?? raw?.meta?.properties?.entityId;
          
          
          
          if (experimentId) {
            idToFetch = experimentId;
          } else {
            console.warn('[resolveBreadcrumb] ⚠️ Could not extract experiment ID from version!');
          }
        }

        
        
        // Fetch from API - pass the entity ID
        const trail = await api.fetchBreadcrumb(
          idToFetch,
          node.entityType,
          false,
          config.forLinkEntity
        );
        
        
        // Prepend HOME/Folders to the trail
        const homeItem: BreadcrumbNode = {
          _id: null,
          name: 'Folders',
          entityType: 'HOME',
        };
        
        // Enrich each breadcrumb item with minimal node info for navigation
        const enrichedTrail: BreadcrumbNode[] = [
          {
            ...homeItem,
            node: {
              id: '',
              name: 'Folders',
              entityType: 'HOME',
              kind: 'folder',
              createdBy: undefined,
              createdAt: undefined,
              raw: {},
            } as ExplorerNode,
          },
          ...trail.map((crumb, idx) => {
            // Determine the kind based on entityType
            const isFolderType = crumb.entityType === 'FOLDER' || crumb.entityType === 'FLEXI_FOLDER';
            const kind: 'folder' | 'entity' | 'version' = isFolderType ? 'folder' : 'entity';
            
            return {
              ...crumb,
              node: {
                id: crumb._id || '',
                name: crumb.name,
                entityType: crumb.entityType,
                kind,
                createdBy: undefined,
                createdAt: undefined,
                raw: {},
              } as ExplorerNode,
            };
          }),
        ];
        
        // Store in cache for future navigation
        breadcrumbCache.current.set(node.id, enrichedTrail);
        return enrichedTrail;
      } catch (err) {
        console.warn('Failed to resolve breadcrumb:', err);
        // Degrade gracefully - return minimal trail with node attached
        return [
          {
            _id: node.id,
            name: node.name,
            entityType: node.entityType,
            node,
          },
        ];
      }
    },
    []
  );

  /**
   * Navigate to a node (folder, entity, or HOME)
   * This is the main navigation function
   */
  const navigateTo = useCallback(
    async (node: ExplorerNode | 'HOME', opts?: { page?: number; limit?: number; sortField?: 'name' | 'entityType' | 'createdBy' | 'createdAt'; sortOrder?: 'asc' | 'desc'; searchTerm?: string }) => {
      const generation = ++navigationGenerationRef.current;
      setState((s) => ({ ...s, loading: true, error: null }));

      try {
        let children: ExplorerNode[] = [];
        let breadcrumb: BreadcrumbNode[] = [];
        let total = 0;

        const effectivePage = opts?.page ?? queryStateRef.current.page;
        const effectiveLimit = opts?.limit ?? queryStateRef.current.limit;
        const effectiveSortField = opts?.sortField ?? queryStateRef.current.sortField;
        const effectiveSortOrder = opts?.sortOrder ?? queryStateRef.current.sortOrder;
        // Leaving HOME (folder/entity drill-in) always clears search so a
        // pending HOME search debounce cannot bounce the user back to root.
        const effectiveSearchTerm =
          node === 'HOME'
            ? (opts?.searchTerm !== undefined ? opts.searchTerm : queryStateRef.current.searchTerm)
            : '';

        // Fetch children based on node type
        if (node === 'HOME') {
          const rootResponse = await api.fetchRootFolders({
            entityTypes: config.mode === 'select-folder' ? [] : config.entityTypes,
            selectFolderOnly: config.mode === 'select-folder',
            page: effectivePage,
            limit: effectiveLimit,
            sortField: effectiveSortField,
            sortOrder: effectiveSortOrder,
            searchTerm: effectiveSearchTerm,
            forLinkEntity: config.forLinkEntity,
            showArchived: config.showArchived,
            skipAudit: config.skipAudit,
          });
          children = rootResponse.items;
          total = rootResponse.pagination.total;
          breadcrumb = [{ _id: null, name: 'Folders', entityType: 'HOME' }];
        } else if (node.kind === 'folder' || node.entityType === 'FOLDER' || node.entityType === 'FLEXI_FOLDER') {
          // It's a folder - fetch what's inside
          children = await api.fetchFolderChildren(
            node.id,
            node.entityType,
            {
              entityTypes: config.mode === 'select-folder' ? [] : config.entityTypes,
              selectFolderOnly: config.mode === 'select-folder',
              forLinkEntity: config.forLinkEntity,
              showArchived: config.showArchived,
              skipAudit: config.skipAudit,
            }
          );
          total = children.length;
          breadcrumb = await resolveBreadcrumb(node);
        } else if (node.kind === 'entity' && config.mode === 'import') {
          // It's an entity (workflow, textfile, etc) - fetch versions (import mode only)
          children = await api.fetchEntityVersions(
            node.entityType,
            node,
            { excludeVersionId: config.excludeVersionId, forLinkEntity: config.forLinkEntity }
          );
          total = children.length;
          breadcrumb = await resolveBreadcrumb(node);
        }

        // A newer navigation started while this request was in flight — drop result
        if (generation !== navigationGenerationRef.current) {
          return;
        }

        // Update state with new data
        setState((s) => ({
          ...s,
          currentNode: node,
          children,
          breadcrumbTrail: breadcrumb,
          page: effectivePage,
          limit: effectiveLimit,
          total,
          sortField: effectiveSortField,
          sortOrder: effectiveSortOrder,
          loading: false,
        }));
        queryStateRef.current = {
          page: effectivePage,
          limit: effectiveLimit,
          sortField: effectiveSortField,
          sortOrder: effectiveSortOrder,
          searchTerm: effectiveSearchTerm,
        };
      } catch (err: any) {
        if (generation !== navigationGenerationRef.current) {
          return;
        }
        setState((s) => ({
          ...s,
          loading: false,
          error: err.message || 'Failed to load data',
        }));
      }
    },
    [config, resolveBreadcrumb]
  );

  const setPage = useCallback(
    async (page: number) => {
      await navigateTo(state.currentNode, { page });
    },
    [navigateTo, state.currentNode]
  );

  const setLimit = useCallback(
    async (limit: number) => {
      await navigateTo(state.currentNode, { page: 1, limit });
    },
    [navigateTo, state.currentNode]
  );

  const setSearch = useCallback(
    async (term: string) => {
      if (term === queryStateRef.current.searchTerm) return; // no-op if unchanged
      await navigateTo(state.currentNode, { page: 1, searchTerm: term });
    },
    [navigateTo, state.currentNode]
  );

  const setSort = useCallback(
    async (sortField: 'name' | 'entityType' | 'createdBy' | 'createdAt', sortOrder: 'asc' | 'desc') => {
      await navigateTo(state.currentNode, { page: 1, sortField, sortOrder });
    },
    [navigateTo, state.currentNode]
  );

  /**
   * Toggle selection (for multiple: true mode)
   * Adds or removes item from selection Map
   */
  const toggleSelection = useCallback((node: ExplorerNode) => {
    setState((s) => {
      const next = new Map(s.selection);
      if (next.has(node.id)) {
        next.delete(node.id);
      } else {
        next.set(node.id, node);
      }
      return { ...s, selection: next };
    });
  }, []);

  /**
   * Get all selected items as array
   */
  const getSelectedItems = useCallback(() => {
    return Array.from(state.selection.values());
  }, [state.selection]);

  /**
   * Clear all selections (for multiple: true mode)
   */
  const clearSelection = useCallback(() => {
    setState((s) => ({
      ...s,
      selection: new Map(),
    }));
  }, []);

  return {
    state,
    navigateTo,
    setPage,
    setLimit,
    setSort,
    setSearch,
    toggleSelection,
    getSelectedItems,
    clearSelection,
  };
}
